import React, {useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo} from "react";
import {useSearchParams} from 'react-router-dom';
import {Select, Input, Switch} from "antd";
import {generateYearOptions} from "@/composable/useDates";
import {transformListForSelect} from "@/utils/list";
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';

import debounce from "lodash.debounce";

import {Sort} from '@/constants/filters'

import { setDataToStorage, getDataFromStorage, removeFromStorage } from "@/utils/app";

import api from "@/api/index";

import {Statuses} from '@/constants/filters'

import "./styles/filters-home.scss";

interface Props {
    add: () => void;
    onChangeSearchText: (str: string) => void;
    update: (
        type: string | number | null,
        category: string | number | null,
        year: number | string | null,
        status: string | number | null,
        orderBy: string | null,
        mine: boolean
    ) => void;
    updateOption: (prop: string, value: any) => void;
    updateFilterAll: (data) => void
}

const FiltersHome = forwardRef(({add, onChangeSearchText, update, updateOption, updateFilterAll}: Props, ref) => {
    const [name, setName] = useState<string>();
    const [category, setCategory] = useState<string | number | null>(null);
    const [year, setYear] = useState<string | number | null>(null);
    const [status, setStatus] = useState<string | number | null>(null);
    const [orderBy, setOrderBy] = useState<string | null>(null)
    const [mine, setMine] = useState<boolean>(false)
    const [sortType, setSortType] = useState<string | null>(null)

    const [isInitSearchParams, setIsInitSearchParams] = useState<boolean>(true)
    const [isReadLocalStorage, setIsReadLocalStorage] = useState<boolean>(false)

    const [searchParams, setSearchParams] = useSearchParams()

    const [categories, setCategories] = useState<unknown[]>([]);
    const [listCountries, setListIndex] = useState<any[]>([])

    const storageName = 'Filter'

    const debouncedUpdateOption = useCallback(
        debounce((value: string) => {
            updateOption("name", value)
        }, 300),
        []
    );

    const statuses = Statuses;

    const handleChangeName = (val: string) => {
        setName(val)
        debouncedUpdateOption(val)
    }

    const getCategories = async () => {
        try {
            const {data} = await api.categories.getCategoriesContentManager();
            if (data?.data?.categories) {
                const listCategories = transformListForSelect(data.data.categories, "title", "id");
                listCategories.unshift({
                    value: null, label: 'Все категории', id: null
                })

                const countries = data.data.countries || []

                setListIndex(3)
                setCategories(listCategories);
            }
        } catch (err) {
            console.error(err);
            setCategories([]);

        } finally {
            readStorageVal()
        }
    };

    const clearAll = () => {
        setName(null);
        setCategory(null)
        setYear(null)
        setStatus(null);
        setMine(false)
    };

    const updateUrl = (filterKey: string, filterValue: string | number | boolean) => {
        const params = new URLSearchParams(searchParams.toString());

        if (filterValue === null || filterValue === undefined || filterValue === '') {
            params.delete(filterKey);
        } else {
            params.set(filterKey, filterValue)
        }

        setSearchParams(params)
    }

    useImperativeHandle(ref, () => ({
        clearAll,
    }));

    useEffect(() => {
        getCategories();
    }, []);

    const readStorageVal = () => {
        if(!isReadLocalStorage) {
            const storageData = getDataFromStorage(storageName)
            const storageVal = JSON.parse(storageData)

            if(storageVal) {
                if (storageVal.year) setYear(storageVal.year);
                if (storageVal.category) setCategory(Number(storageVal.category));
                if (storageVal.status) setStatus(storageVal.status);
                if (storageVal.mine) setMine(storageVal.mine);
                if (storageVal.sort_by) setOrderBy(storageVal.sort_by);
                if (storageVal.sortType) setSortType(storageVal.sortType);

                if(!storageVal.sortType) {
                    if(!storageVal.sortType && (!storageVal.sort_by || storageVal.sort_by === 'created_at')) {
                        setSortType('desc')
                    }
                }

                updateFilterAll({
                    year: storageVal.year || null,
                    category: storageVal.category || null,
                    status: storageVal.status || null,
                    sort_by: storageVal.sort_by || null,
                    name: null,
                    mine: storageVal.mine || false
                })
            } else {
                setSortType('desc')
                setOrderBy('created_at')

                updateFilterAll({
                    year: null,
                    category: null,
                    status: null,
                    sort_by: null,
                    name: null,
                    mine: null
                })
            }

            setIsReadLocalStorage(true)
            setIsInitSearchParams(false)
        }
    }

    const handleSwitch = (val) => {
        setMine(val)
    }

    const handleSortType = () => {
        switch(sortType) {
            case null:
                setSortType('asc');
                break;

            case 'asc':
                setSortType('desc');
                break;

            default:
                setSortType(null)
        }
    }

    const classesSortType = useMemo(() => {
        const baseClasses  = 'filters-home__btn-sort app-btn-icon';
        let activeClasses  = ''

        if(sortType !== null) {
            activeClasses += 'active'
        }

        return `${baseClasses} ${activeClasses}`
    }, [sortType])

    useEffect(() => {
        if(!isInitSearchParams) {
            setIsInitSearchParams(true)
        }
    }, [isInitSearchParams])


    useEffect(() => {
        if(isReadLocalStorage && isInitSearchParams) {
            const values = {}

            if (year) values.year = year;
            if (category) values.category = Number(category);
            if (status) values.status = status;
            if (orderBy) values.sort_by = orderBy;
            if (name) values.name = name;
            if (mine) values.mine = mine.toString();
            if (sortType) values.sortType = sortType;


            setDataToStorage(storageName, JSON.stringify(values))
            updateFilterAll({
                year: year || null,
                category: category || null,
                status: status || null,
                sort_by: orderBy || null,
                name: name || null,
                mine: mine || null,
                sortType: sortType || null
            })
        }

    }, [name, category, year, status, orderBy, mine, sortType])

    return (
        <div className={'filters-home__wrap'}>
            <div className="filters-home">
                <div className="filters-home__top">
                    <Input
                        onChange={(e) => {
                            handleChangeName(e.target.value)
                            updateUrl('name', e.target.value);
                        }}
                        placeholder="Поиск по названию"
                        value={name}
                    />
                </div>

                <div className="filters-home__bottom">
                    <div className="form-group">
                        <label className="form-group__label" htmlFor="Year">
                            Год
                        </label>
                        <Select
                            id="Year"
                            className="app-select"
                            value={year}
                            onChange={(val) => {
                                setYear(val);
                            }}
                            placeholder="Выберите год"
                            options={generateYearOptions(
                                2000,
                                new Date().getFullYear(), // например, 2025
                                {order: 'desc', valueAsString: true},
                                true
                            )}
                            allowClear
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-group__label" htmlFor="Category">
                            Категория
                        </label>
                        <Select
                            id="Category"
                            className="app-select"
                            value={category}
                            onChange={(val) => {
                                setCategory(val);
                            }}
                            placeholder="Выберите категорию"
                            options={categories}
                            allowClear
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-group__label" htmlFor="Status">
                            Статус
                        </label>
                        <Select
                            id="Status"
                            className="app-select"
                            value={status}
                            onChange={(val) => {
                                setStatus(val);
                            }}
                            placeholder="Выберите статус"
                            options={statuses}
                            allowClear
                        />
                    </div>

                    <div className={'form-group'}>
                        <label className={'form-group__label'}>
                            Только свое
                        </label>
                        <div>
                            <Switch checked={mine} onChange={(event) => {
                                handleSwitch(event)
                            }} />
                        </div>
                    </div>
                </div>

                <div className="filters-home__footer">
                    <div className="form-group form-group__sort">
                        <div className={'form-group__sort__container'}>
                            <label className="form-group__label" htmlFor="OrderBy">
                                Сортировка
                            </label>

                            <div className={'filters-home__sort-group'}>
                                <button className={classesSortType} onClick={handleSortType}>
                                    {
                                        sortType === null && <SortAscendingOutlined />
                                    }

                                    {
                                        sortType === 'asc' && <SortAscendingOutlined />
                                    }

                                    {
                                        sortType === 'desc' && <SortDescendingOutlined />
                                    }
                                </button>
                            </div>
                        </div>
                        <Select
                            id="OrderBy"
                            className="app-select"
                            value={orderBy}
                            onChange={(val) => {
                                setOrderBy(val);
                            }}
                            placeholder="Сортировать по"
                            options={Sort}
                            allowClear
                        />
                    </div>
                </div>
            </div>

            <div className={"filters-home__add"}>
                <button className="app-btn filters-home__add__btn" onClick={add}>
                    Добавить
                </button>
            </div>
        </div>
    );
});

export default FiltersHome;